import { useCallback, useMemo, useState } from "react";
import { getAttachmentSelectionKey } from "../utils";

/**
 * Centralizes attachment selection state using stable `${id}::${fileId}` keys
 * instead of the previous parent/children array-index system. Selection keys
 * are derived from the attachment objects themselves, so they survive
 * reordering, scrolling and data refreshes.
 *
 * The returned toggle handlers keep the legacy `(item, index, versionIndex)`
 * signature for drop-in compatibility with the shared render layer
 * (`ListAttachments`, `AttachmentRow`, `VersionRow`, `CreateActivity`), but the
 * positional index arguments are ignored. Both call shapes are supported:
 * a grouped `{ parent, version }` item or a bare attachment object.
 *
 * @returns {{
 *   activeKeys: Set<string>,
 *   toggleActiveCheckBox: (fileOrParent: object) => void,
 *   toggleActiveVersionCheckBox: (version: object) => void,
 *   clearActive: () => void,
 *   resetSelection: (keys?: Array<string|null>) => void,
 *   applySelection: (compiledFiles: Array) => Array,
 * }}
 */
export const useAttachmentSelection = () => {
  const [activeKeys, setActiveKeys] = useState(() => new Set());

  const toggleKey = useCallback((attachment) => {
    const key = getAttachmentSelectionKey(attachment);
    if (!key) return;
    setActiveKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const toggleActiveCheckBox = useCallback(
    (fileOrParent) => {
      // Accepts either a grouped `{ parent, version }` item or a bare parent.
      const parent = fileOrParent?.parent ?? fileOrParent;
      toggleKey(parent);
    },
    [toggleKey]
  );

  const toggleActiveVersionCheckBox = useCallback(
    (version) => toggleKey(version),
    [toggleKey]
  );

  const clearActive = useCallback(() => setActiveKeys(new Set()), []);

  const resetSelection = useCallback((keys = []) => {
    setActiveKeys(new Set(keys.filter(Boolean)));
  }, []);

  const applySelection = useCallback(
    (compiledFiles) => {
      if (!Array.isArray(compiledFiles)) return [];
      return compiledFiles.map((item) => ({
        ...item,
        parent: item?.parent
          ? {
              ...item.parent,
              isActive: activeKeys.has(getAttachmentSelectionKey(item.parent)),
            }
          : item?.parent,
        version: Array.isArray(item?.version)
          ? item.version.map((v) =>
              v
                ? { ...v, isActive: activeKeys.has(getAttachmentSelectionKey(v)) }
                : v
            )
          : item?.version,
      }));
    },
    [activeKeys]
  );

  return useMemo(
    () => ({
      activeKeys,
      toggleActiveCheckBox,
      toggleActiveVersionCheckBox,
      clearActive,
      resetSelection,
      applySelection,
    }),
    [
      activeKeys,
      toggleActiveCheckBox,
      toggleActiveVersionCheckBox,
      clearActive,
      resetSelection,
      applySelection,
    ]
  );
};
